import React, { useEffect, useState, useCallback } from 'react'
import { toast } from 'react-toastify';
import validator from 'validator'
import format from 'date-fns/format'

import { Auth, API, Storage } from 'aws-amplify'
import { updateAuthAccount } from '../../../managers/graphql/mutations'

import { useAppDispatch, useAppSelector } from '../../../hooks';
import { cognitoConfirmSignUp, reloadAuthAccount, updateProfilePictureURI } from '../../../managers/redux/slices/authAccount';

// mui
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField'
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl'
import CircularProgress from '@mui/material/CircularProgress';
import PhoneNumberInput from 'material-ui-phone-number';

import ProfileAvatar from '../../../components/data-display/ProfileAvatar'

import { AuthAccount } from '../../../API'

import { useDropzone } from 'react-dropzone'

interface ManageProfileProps {
    visible: boolean
    onClose: () => void
}

export default function ManageProfile(props: ManageProfileProps) {

    const dispatch = useAppDispatch()

    useEffect(() => {
        setVisible(props.visible)
    }, [props.visible])

    function handleClose() {
        setVisible(false)
        props.onClose()
    }

    const [visible, setVisible] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(false)

    const [firstName, setFirstName] = useState<string | null>(null)
    const [lastName, setLastName] = useState<string | null>(null)
    const [email, setEmail] = useState<string | null>(null)
    const [phoneNumber, setPhoneNumber] = useState<string | null>(null)
    const [dateOfBirth, setDateOfBirth] = useState<string | null>(null)
    const [gender, setGender] = useState<string | null>(null)
    const [selfDescribedGender, setSelfDescribedGender] = useState<string | null>(null)
    const [website, setWebsite] = useState<string | null>(null)
    const [bio, setBio] = useState<string | null>(null)

    const [changeImage, setChangeImage] = useState<boolean>(false)
    const [uploadedFile, setUploadedFile] = useState<any>(null)

    const [workExperienceFile, setWorkExperienceFile] = useState<File | null>(null)
    const [fileSelected, setFileSelected] = useState<boolean>(false)

    const authAccount: AuthAccount | null | undefined = useAppSelector(state => state.authAccount.data)

    const onDrop = useCallback(async acceptedFiles => {
        // Do something with the files
        console.log(acceptedFiles[0])
        try {
            if (authAccount) {
                await Storage.put(`authAccounts/${authAccount.id}/profilePicture`, acceptedFiles[0], {
                    contentType: acceptedFiles[0].type
                })
                dispatch(updateProfilePictureURI(`authAccounts/${authAccount.id}/profilePicture`))
                setChangeImage(false)
            }
        } catch (err) {
            console.log(err)
        }
    }, [])

    const { acceptedFiles, getRootProps, getInputProps, isDragActive } = useDropzone({ maxFiles: 1, accept: 'image/jpeg,image/png', onDrop: onDrop, maxSize: 5242880 });

    async function _handleSaveChanges() {
        if (authAccount) {
            if (workExperienceFile) {
                try {
                    await Storage.put(`authAccounts/${authAccount.id}/resume`, workExperienceFile, {
                        contentType: workExperienceFile.type
                    })
                } catch (err) {
                    console.log(err)
                    toast.error('Error uploading file. Contact support or try again later.', {
                        position: "bottom-right",
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                        containerId: 'main'
                    });
                }
            }

            var validatedPhoneNumber: string | null = null
            var validatedWebsite: string | null = null

            if (phoneNumber && validator.isMobilePhone(phoneNumber)) validatedPhoneNumber = phoneNumber
            if (website && validator.isURL(website)) validatedWebsite = website

            const updateAuthAccountResponse: any = await API.graphql({
                query: updateAuthAccount,
                variables: {
                    input: {
                        id: authAccount.id,
                        firstName: firstName,
                        lastName: lastName,
                        phoneNumber: validatedPhoneNumber,
                        dateOfBirth: dateOfBirth ? dateOfBirth.toString() : null,
                        gender: gender,
                        website: validatedWebsite,
                        resumeURI: workExperienceFile ? `authAccounts/${authAccount.id}/resume` : fileSelected ? authAccount.resumeURI : null,
                        bio: bio
                    }
                }
            })

            if (updateAuthAccountResponse.data && updateAuthAccountResponse.data.updateAuthAccount) {
                toast.success('Saved profile information', {
                    position: "bottom-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    containerId: 'main'
                });
                dispatch(reloadAuthAccount())
                setEmail(null)
                setFirstName(null)
                setLastName(null)
                setPhoneNumber(null)
                setDateOfBirth(null)
                setWebsite(null)
                setFileSelected(false)
                setWorkExperienceFile(null)
                props.onClose()
            }
        }
    }

    useEffect(() => {
        if (authAccount && authAccount.primaryEmail) {
            setEmail(authAccount.primaryEmail)
        }
        if (authAccount && authAccount.firstName) {
            setFirstName(authAccount.firstName)
        }
        if (authAccount && authAccount.lastName) {
            setLastName(authAccount.lastName)
        }
        if (authAccount && authAccount.phoneNumber) {
            setPhoneNumber(authAccount.phoneNumber)
        }
        if (authAccount && authAccount.dateOfBirth) {
            setDateOfBirth(authAccount.dateOfBirth)
        }
        if (authAccount && authAccount.gender) {
            setGender(authAccount.gender)
        }
        if (authAccount && authAccount.website) {
            setWebsite(authAccount.website)
        }
        if (authAccount && authAccount.resumeURI) {
            setFileSelected(true)
        }
        if (authAccount && authAccount.bio) {
            setBio(authAccount.bio)
        }
    }, [authAccount])

    return (
        <div>
            <Dialog
                open={visible}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                sx={{ zIndex: 99 }}
            >
                <DialogTitle id="alert-dialog-title">
                    Manage your profile
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description" className="mb-3">
                        Edit details about your profile here. A lot of the things on your profile are used by the organization during the screening process when you apply for a volunteer opportunity.
                    </DialogContentText>
                    <div className="d-flex flex-row justify-content-between align-items-center">
                        {
                            !changeImage ?
                                <ProfileAvatar authAccountID={authAccount ? authAccount.id : null} className="my-3" size={100} />
                                :
                                <section className="my-3 d-flex flex-column justify-content-center" style={{ borderWidth: 1, borderStyle: 'dashed', borderColor: 'black', height: 100, width: 100, borderRadius: 50 }}>
                                    <div {...getRootProps({ className: 'dropzone h-100 w-100' })} style={{ backgroundImage: uploadedFile ? uploadedFile.preview : null }}>
                                        <input {...getInputProps()} />
                                        {
                                            isDragActive ?
                                                <p style={{ width: '100%', textAlign: 'center' }}>Drop the files here</p>
                                                :
                                                <p style={{ width: '100%', textAlign: 'center' }}>Drag an image here, or click to select a file.</p>
                                        }
                                    </div>
                                </section>
                        }
                        {
                            !changeImage ?
                                <Button onClick={() => { setChangeImage(true) }}>
                                    Change Image
                                </Button>
                                :
                                <Button onClick={() => { setChangeImage(false) }}>
                                    Cancel
                                </Button>
                        }
                    </div>

                    <TextField
                        id="email"
                        disabled
                        variant="outlined"
                        placeholder="Email"
                        value={email}
                        inputProps={{ style: { fontSize: 16 } }}
                        fullWidth
                        className="my-2"
                    />
                    <TextField
                        id="firstName"
                        variant="outlined"
                        type="text"
                        placeholder="First name"
                        inputProps={{ style: { fontSize: 16 } }}
                        fullWidth
                        onChange={(e) => {
                            setFirstName(e.target.value)
                        }}
                        value={firstName}
                        className="my-2"
                        label="First name"
                    />
                    <TextField
                        id="lastName"
                        variant="outlined"
                        type="text"
                        placeholder="Last name"
                        inputProps={{ style: { fontSize: 16 } }}
                        fullWidth
                        onChange={(e) => {
                            setLastName(e.target.value)
                        }}
                        value={lastName}
                        className="my-2"
                        label="Last name"
                    />
                    <PhoneNumberInput
                        defaultCountry={'us'}
                        onChange={(e) => { if (typeof e == 'string') setPhoneNumber(e) }}
                        value={phoneNumber}
                        variant="outlined"
                        fullWidth
                        className="my-2"
                        label="Phone number"
                    />
                    <TextField
                        id="dateOfBirth"
                        variant="outlined"
                        type="date"
                        placeholder="Date of birth"
                        inputProps={{ style: { fontSize: 16 } }}
                        fullWidth
                        onChange={(e) => {
                            setDateOfBirth(e.target.value)
                        }}
                        value={dateOfBirth}
                        className="my-2"
                        label="Date of birth"
                    />
                    <FormLabel className="mt-2">Gender</FormLabel>
                    <RadioGroup
                        row
                        name="genderSelection"
                        className="mb-2"
                        value={gender}
                        onChange={(e) => {
                            setGender((e.target as HTMLInputElement).value)
                        }}
                    >
                        <FormControlLabel value="female" control={<Radio />} label="Female" />
                        <FormControlLabel value="male" control={<Radio />} label="Male" />
                        <FormControlLabel value="nonBinary" control={<Radio />} label="Non-binary" />
                        <FormControlLabel value="preferNotToDisclose" control={<Radio />} label="Prefer not to disclose" />
                        <FormControlLabel value="preferToSelfDescribe" control={<Radio />} label="Prefer to self-descibe" />
                        {
                            gender == 'preferToSelfDescribe' &&
                            <TextField
                                id="selfDescribedGender"
                                variant="outlined"
                                label="Gender"
                                value={selfDescribedGender}
                                onChange={(e) => setSelfDescribedGender(e.target.value)}
                                inputProps={{ style: { fontSize: 16 } }}
                                fullWidth
                                className="my-2"
                            />
                        }
                    </RadioGroup>
                    <TextField
                        id="website"
                        variant="outlined"
                        type="text"
                        placeholder="Your personal website or LinkedIn"
                        inputProps={{ style: { fontSize: 16 } }}
                        fullWidth
                        onChange={(e) => {
                            setWebsite(e.target.value)
                        }}
                        value={website}
                        className="my-2"
                        label="Website"
                    />
                    {
                        fileSelected ?
                            <div className="d-flex flex-row justify-content-start align-items-center">
                                <Typography variant="body1">document uploaded</Typography>
                                <Button onClick={() => { setFileSelected(false); setWorkExperienceFile(null) }} variant="outlined" className="mx-2">Upload new file</Button>
                            </div>
                            :
                            <input
                                id="workExperienceFile"
                                type="file"
                                name="workExperience"
                                accept=".pdf,.doc,.docx"
                                placeholder="Upload your resume or CV"
                                onChange={(e) => {
                                    if (e.target.files && e.target.files.length == 1) {
                                        setWorkExperienceFile(e.target.files[0])
                                        console.log(e.target.files[0])
                                        setFileSelected(true)
                                    } else {
                                        setFileSelected(false)
                                    }
                                }}
                                className="my-2"
                            />
                    }
                    <TextField
                        id="bio"
                        variant="outlined"
                        type="text"
                        multiline
                        minRows={3}
                        placeholder="A little bit about yourself to help organizations get to know you better"
                        inputProps={{ style: { fontSize: 16 } }}
                        fullWidth
                        onChange={(e) => {
                            setBio(e.target.value)
                        }}
                        value={bio}
                        className="my-2"
                        label="Bio"
                    />
                </DialogContent>
                <DialogActions className="mt-3">
                    {
                        !loading ?
                            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center' }}>
                                <Button onClick={handleClose}>Cancel</Button>
                                <Button
                                    onClick={_handleSaveChanges}
                                    variant="contained"
                                >
                                    Save
                                </Button>
                            </div>
                            :
                            <CircularProgress />
                    }
                </DialogActions>
            </Dialog>
            <div id="map"></div>
        </div>
    )
}